import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { parseApiDateTime } from '@/lib/dateTime';
import { getDefaultDoctorImage, handleImageFallback } from '@/lib/defaultImages';
import { isDoctorAccount } from '@/lib/userRoles';
import { normalizeAppointmentTypes, resolveMapHref } from '@/lib/bookingUtils';
import { toast } from 'sonner';
import { 
  Star, 
  MapPin, 
  Clock, 
  Phone,
  ChevronLeft,
  Calendar as CalendarIcon,
  Check,
  Loader2,
  Users,
  UserPlus,
  PhoneCall,
  CircleDot,
  Layers3,
  House,
  TimerReset
} from 'lucide-react';
import { doctorService, appointmentService, patientService, addressService, getLocalizedValue, normalizeImageValue, formatCurrencyAmount } from '@/services/api';
import { format, startOfToday, addDays } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';

// Helper function to get display name from doctor object
const getDoctorDisplayName = (doctor, lang) => {
  if (!doctor) return '';
  // If name is already a string, return it
  if (typeof doctor.name === 'string') return doctor.name;
  // If name is an object with lang keys
  if (doctor.name && typeof doctor.name === 'object') {
    return doctor.name[lang] || doctor.name['ar'] || doctor.name['en'] || '';
  }
  // Fallback to name_ar or name_en
  if (lang === 'ar' && doctor.name_ar) return doctor.name_ar;
  if (doctor.name_en) return doctor.name_en;
  return '';
};

// Helper to get specialty name
const getSpecialtyName = (doctor, lang) => {
  if (!doctor) return '';
  // Check specialties array first
  if (doctor.specialties && doctor.specialties.length > 0) {
    const spec = doctor.specialties[0];
    if (typeof spec.name === 'string') return spec.name;
    if (spec.name && typeof spec.name === 'object') {
      return spec.name[lang] || spec.name['ar'] || spec.name['en'] || '';
    }
  }
  // Check specialty object
  if (doctor.specialty) {
    if (typeof doctor.specialty.name === 'string') return doctor.specialty.name;
    if (doctor.specialty.name && typeof doctor.specialty.name === 'object') {
      return doctor.specialty.name[lang] || doctor.specialty.name['ar'] || doctor.specialty.name['en'] || '';
    }
  }
  return '';
};

const getPatientDisplayName = (patient, lang) => {
  if (!patient) return '';

  const firstName = getLocalizedValue(patient.first_name, lang) || patient.first_name?.en || patient.first_name || '';
  const lastName = getLocalizedValue(patient.last_name, lang) || patient.last_name?.en || patient.last_name || '';
  const fullName = `${firstName} ${lastName}`.trim();

  return fullName || patient.name || '';
};

const getPatientAddressLabel = (address, lang) => {
  if (!address) return '';

  const primary =
    getLocalizedValue(address.address, lang) ||
    address.address ||
    getLocalizedValue(address.description, lang) ||
    address.description ||
    '';
  const secondary =
    getLocalizedValue(address.description, lang) ||
    address.description ||
    '';

  if (secondary && secondary !== primary) {
    return `${primary} - ${secondary}`;
  }

  return primary;
};

const getTelHref = (phone) => {
  const normalizedPhone = String(phone || '')
    .trim()
    .replace(/[^\d+]/g, '')
    .replace(/(?!^)\+/g, '');

  return normalizedPhone ? `tel:${normalizedPhone}` : '';
};

const getMapHref = resolveMapHref;

const extractAddressRows = (payload) => {
  if (Array.isArray(payload)) return payload;

  const data = payload?.data ?? payload;
  if (Array.isArray(data)) return data;

  const candidates = [
    data?.addresses,
    data?.items,
    data?.records,
    data?.results,
    payload?.addresses,
    payload?.items,
    payload?.records,
    payload?.results,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate;
    }
  }

  return [];
};

const getServiceDisplayName = (service, lang) => {
  if (!service) return '';

  if (typeof service.name === 'string') {
    return service.name;
  }

  const localizedName = getLocalizedValue(service.name, lang);
  return localizedName || service.name_ar || service.name_en || '';
};

const normalizeDoctorServices = (services, lang) => {
  if (!Array.isArray(services)) return [];

  return services.map((service) => {
    const children = normalizeDoctorServices(service.children || service.sub_services || [], lang);
    const isActive = service.is_active ?? service.isActive ?? true;
    const comingSoon = service.coming_soon ?? service.comingSoon ?? false;
    const durationMinutes = Number(service.duration_minutes ?? service.durationMinutes ?? 0);
    const price = Number(service.price ?? service.consultation_fee ?? 0);
    const hasSubService = Boolean(
      service.has_sub_service ?? service.hasSubService ?? children.length > 0
    );

    return {
      ...service,
      name: getServiceDisplayName(service, lang),
      description: getLocalizedValue(service.description, lang) || service.description || '',
      isActive,
      comingSoon,
      durationMinutes,
      price,
      hasSubService,
      children,
      canBookDirectly: children.length === 0,
    };
  });
};

const extractServiceRows = (payload) => {
  if (Array.isArray(payload)) return payload;

  const data = payload?.data ?? payload;
  if (Array.isArray(data)) return data;

  const candidates = [
    data?.services,
    data?.data,
    data?.items,
    data?.records,
    data?.results,
    payload?.services,
    payload?.items,
    payload?.records,
    payload?.results,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate;
    }
  }

  return [];
};

const buildDoctorServicesTree = async (doctorId, lang) => {
  const mapServiceWithChildren = async (service) => {
    const normalizedBase = normalizeDoctorServices([service], lang)[0];
    if (!normalizedBase) return null;

    if (!normalizedBase.hasSubService) {
      return normalizedBase;
    }

    try {
      const childrenResponse = await doctorService.getServiceChildren(normalizedBase.id);
      const childRows = extractServiceRows(childrenResponse?.data);
      const children = (
        await Promise.all(childRows.map((child) => mapServiceWithChildren(child)))
      ).filter(Boolean);

      return {
        ...normalizedBase,
        hasSubService: children.length > 0 || normalizedBase.hasSubService,
        children,
        canBookDirectly: children.length === 0,
      };
    } catch (error) {
      return normalizedBase;
    }
  };

  try {
    const response = await doctorService.getServices(doctorId);
    const serviceRows = extractServiceRows(response?.data);
    const services = (
      await Promise.all(serviceRows.map((service) => mapServiceWithChildren(service)))
    ).filter(Boolean);

    return services.filter((service) => service.id > 0 && service.isActive);
  } catch (error) {
    return [];
  }
};

const getVisibleServices = (services) =>
  services.filter((service) => service.isActive && !service.comingSoon);

const formatPriceLabel = (value, currencySource = {}) =>
  formatCurrencyAmount(value, currencySource);

const getServiceLevelTitle = (levelIndex, lang) => {
  if (levelIndex === 0) {
    return lang === 'ar' ? 'الخدمات الرئيسية' : 'Main services';
  }

  if (levelIndex === 1) {
    return lang === 'ar' ? 'الخدمات الفرعية' : 'Sub services';
  }

  return lang === 'ar' ? `المستوى ${levelIndex + 1}` : `Level ${levelIndex + 1}`;
};

const ServiceChoiceCard = ({ service, selected, onClick, showChildrenHint = false, compact = false, lang, currencySource }) => {
  const activeChildrenCount = getVisibleServices(service.children || []).length;
  const description = service.description?.trim();

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'group relative w-full overflow-hidden rounded-2xl border text-start transition-all duration-200',
        compact ? 'min-h-[220px] p-5' : 'p-5',
        selected
          ? 'border-teal-500/60 bg-teal-500/10 shadow-[0_18px_40px_rgba(20,184,166,0.12)] ring-1 ring-teal-400/40'
          : 'border-slate-200 bg-white/90 hover:-translate-y-0.5 hover:border-slate-300 hover:bg-white dark:border-slate-700 dark:bg-slate-800/80 dark:hover:border-slate-500 dark:hover:bg-slate-800'
      )}
    >
      <div className={cn(
        'absolute inset-x-0 top-0 h-1 opacity-90',
        selected ? 'bg-gradient-to-r from-teal-400 via-cyan-400 to-blue-500' : 'bg-slate-700'
      )} />
      <div className="mb-4 flex items-start justify-between gap-3">
        <span
          className={cn(
            'flex h-8 w-8 items-center justify-center rounded-full border-2 transition-colors',
            selected
              ? 'border-teal-600 bg-teal-50 text-teal-700 dark:border-teal-300 dark:bg-teal-400/15 dark:text-teal-100'
              : 'border-slate-500 text-transparent'
          )}
        >
          <CircleDot className="h-4 w-4" />
        </span>
        {service.price > 0 && (
          <span className="rounded-full bg-slate-200 px-3 py-1 text-xs font-semibold text-slate-700 dark:bg-slate-700/80 dark:text-slate-200">
            {formatPriceLabel(service.price, currencySource)}
          </span>
        )}
      </div>

      <div className="space-y-2">
        <h4 className="text-lg font-bold tracking-tight text-slate-900 dark:text-white">{service.name}</h4>
        <p className="text-sm leading-7 text-slate-600 dark:text-slate-300">
          {description ||
            (showChildrenHint
              ? 'اختر الخدمات الفرعية المناسبة قبل الحجز'
              : 'الخدمة النهائية الجاهزة للحجز مباشرة')}
        </p>
      </div>

      <div className="mt-5 flex flex-wrap gap-2 text-xs font-medium text-slate-500">
        {activeChildrenCount > 0 ? (
          <span className="rounded-full bg-teal-100 px-3 py-1 text-teal-800 ring-1 ring-teal-200 dark:bg-teal-500/15 dark:text-teal-100 dark:ring-teal-300/20">
            {activeChildrenCount} {activeChildrenCount === 1 ? 'Sub service' : 'Sub services'}
          </span>
        ) : service.durationMinutes > 0 ? (
          <span className="rounded-full bg-slate-200 px-3 py-1 text-slate-700 dark:bg-slate-700/80 dark:text-slate-200">
            {service.durationMinutes} {lang === 'ar' ? 'دقيقة' : 'Minutes'}
          </span>
        ) : null}
      </div>
    </button>
  );
};

const ServiceChoiceRow = ({ service, selected, onClick, showChildrenHint = false, lang, currencySource }) => {
  const activeChildrenCount = getVisibleServices(service.children || []).length;
  const description = service.description?.trim();

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'group w-full rounded-[22px] border px-4 py-4 text-start transition-all duration-200',
        selected
          ? 'border-cyan-400/50 bg-cyan-400/10 ring-1 ring-cyan-400/30'
          : 'border-slate-200 bg-white/90 hover:border-slate-300 hover:bg-white dark:border-white/10 dark:bg-white/[0.03] dark:hover:border-white/20 dark:hover:bg-white/[0.05]'
      )}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex items-center gap-3">
            <span
              className={cn(
                'flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 transition-colors',
                selected
                  ? 'border-cyan-600 bg-cyan-50 text-cyan-700 dark:border-cyan-300 dark:bg-cyan-400/15 dark:text-cyan-200'
                  : 'border-slate-500 text-transparent'
              )}
            >
              <CircleDot className="h-4 w-4" />
            </span>
            <h4 className="truncate text-base font-bold tracking-tight text-slate-900 dark:text-white">{service.name}</h4>
          </div>
          <p className="text-sm leading-6 text-slate-600 dark:text-slate-300">
            {description ||
              (showChildrenHint
                ? lang === 'ar'
                  ? 'اختر من هذا المستوى للانتقال إلى المستوى التالي'
                  : 'Choose from this level to open the next level'
                : lang === 'ar'
                  ? 'الخدمة النهائية الجاهزة للحجز مباشرة'
                  : 'Final service ready for direct booking')}
          </p>
          <div className="flex flex-wrap gap-2 text-xs font-medium">
            {service.price > 0 ? (
              <span className="rounded-full bg-slate-200 px-3 py-1 text-slate-700 dark:bg-slate-700/80 dark:text-slate-100">
                {formatPriceLabel(service.price, currencySource)}
              </span>
            ) : null}
            {service.durationMinutes > 0 ? (
              <span className="rounded-full bg-slate-200 px-3 py-1 text-slate-700 dark:bg-slate-700/80 dark:text-slate-100">
                {service.durationMinutes} {lang === 'ar' ? 'دقيقة' : 'Minutes'}
              </span>
            ) : null}
            {activeChildrenCount > 0 ? (
              <span className="rounded-full bg-cyan-100 px-3 py-1 text-cyan-800 ring-1 ring-cyan-200 dark:bg-cyan-400/15 dark:text-cyan-100 dark:ring-cyan-300/20">
                {activeChildrenCount}{' '}
                {lang === 'ar'
                  ? activeChildrenCount === 1
                    ? 'خدمة فرعية'
                    : 'خدمات فرعية'
                  : activeChildrenCount === 1
                    ? 'Sub service'
                    : 'Sub services'}
              </span>
            ) : null}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {showChildrenHint ? (
            <span className="text-sm font-bold text-cyan-700 dark:text-cyan-200">
              {lang === 'ar' ? 'التالي' : 'Next'}
            </span>
          ) : null}
          <span
            className={cn(
              'flex h-10 w-10 items-center justify-center rounded-full border transition-colors',
              selected
                ? 'border-cyan-600/60 bg-cyan-50 text-cyan-700 dark:border-cyan-300/60 dark:bg-cyan-400/15 dark:text-cyan-200'
                : 'border-slate-200 bg-white/80 text-slate-500 dark:border-white/10 dark:bg-white/[0.03] dark:text-slate-400'
            )}
          >
            <CircleDot className="h-4 w-4" />
          </span>
        </div>
      </div>
    </button>
  );
};

const BookingStepCard = ({ step, title, description, children, muted = false }) => (
  <section
    className={cn(
      'space-y-4 rounded-[28px] border p-5',
      muted
        ? 'border-slate-200 bg-slate-100/70 dark:border-white/10 dark:bg-white/[0.03]'
        : 'border-cyan-200 bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(241,245,249,0.94))] dark:border-cyan-400/15 dark:bg-[linear-gradient(180deg,rgba(15,23,42,0.94),rgba(15,23,42,0.78))]'
    )}
  >
    <div className="space-y-2">
      <span className="inline-flex items-center rounded-full border border-slate-200 bg-white/80 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500 dark:border-white/10 dark:bg-white/[0.04] dark:text-slate-300">
        {step}
      </span>
      <div className="space-y-1">
        <h3 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white">{title}</h3>
        {description ? <p className="max-w-2xl text-sm leading-6 text-slate-600 dark:text-slate-400">{description}</p> : null}
      </div>
    </div>
    {children}
  </section>
);

const DoctorDetailPage = () => {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const lang = (i18n.language || 'ar').split('-')[0];
  const isDoctorUser = isDoctorAccount(user);
  
  const [doctor, setDoctor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(0); // Index of selected date
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedServicePath, setSelectedServicePath] = useState([]);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedAppointmentType, setSelectedAppointmentType] = useState('regular');
  const [bookingMode, setBookingMode] = useState('scheduled');
  
  // Ref to prevent duplicate booking submissions
  const isBookingInProgress = useRef(false);
  
  // Patient selection for family member booking
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientsLoading, setPatientsLoading] = useState(false);
  const [patientAddresses, setPatientAddresses] = useState([]);
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState('');

  const locale = lang === 'ar' ? ar : enUS;
  const isRTL = lang === 'ar';
  const doctorPhoneHref = getTelHref(doctor?.phone);
  const doctorMapHref = doctor?.mapHref || getMapHref({ address: doctor?.address });

  // Generate next 7 days for date selection - memoized to prevent re-renders
  const today = React.useMemo(() => startOfToday(), []);
  const dateOptions = React.useMemo(() => 
    Array.from({ length: 7 }, (_, i) => addDays(today, i)), 
    [today]
  );
  const actualSelectedDate = dateOptions[selectedDate];
  const visibleServices = React.useMemo(
    () => getVisibleServices(doctor?.services || []),
    [doctor?.services]
  );
  const serviceLevels = React.useMemo(() => {
    const levels = [
      {
        key: 'main-services',
        levelIndex: 0,
        title: getServiceLevelTitle(0, lang),
        parentLabel: null,
        parentPath: [],
        services: visibleServices,
      },
    ];

    selectedServicePath.forEach((service, index) => {
      const children = getVisibleServices(service.children || []);
      if (!children.length) return;

      levels.push({
        key: `service-level-${service.id}-${index + 1}`,
        levelIndex: index + 1,
        title: getServiceLevelTitle(index + 1, lang),
        parentLabel: service.name,
        parentPath: selectedServicePath.slice(0, index + 1),
        services: children,
      });
    });

    return levels;
  }, [lang, selectedServicePath, visibleServices]);
  const selectedService = React.useMemo(() => {
    if (!selectedServicePath.length) return null;
    const lastService = selectedServicePath[selectedServicePath.length - 1];
    return getVisibleServices(lastService.children || []).length === 0 ? lastService : null;
  }, [selectedServicePath]);
  const selectedServiceId = selectedService?.id || null;
  const requiresFinalServiceSelection = selectedServicePath.length > 0 && !selectedService;
  const isGeneralAppointmentSelected = selectedServicePath.length === 0;
  const isWaitingBooking = bookingMode === 'waiting';
  const homeVisitFee = Number(
    doctor?.home_visit_discount_fee || doctor?.home_visit_fee || 0
  );
  const currentBookingPrice = Number(selectedService?.price || doctor?.consultation_fee || 0)
    + (selectedAppointmentType === 'home_visit' ? homeVisitFee : 0);
  const currentBookingDuration = selectedService?.durationMinutes || doctor?.waiting_time || 30;
  const currentBookingLabel =
    selectedService?.name ||
    (lang === 'ar' ? 'موعد عام' : 'General appointment');
  const bookingSelectionPathLabel = isGeneralAppointmentSelected
    ? currentBookingLabel
    : selectedServicePath.map((service) => service.name).join(' / ');
  const scheduleSelectionLocked = !isWaitingBooking && visibleServices.length > 0 && requiresFinalServiceSelection;
  const availableAppointmentTypes = React.useMemo(() => {
    const types = normalizeAppointmentTypes(doctor?.available_appointment_types);
    const definitions = {
      regular: { label: lang === 'ar' ? 'في العيادة' : 'In Clinic', icon: MapPin },
      online: { label: lang === 'ar' ? 'استشارة عبر الإنترنت' : 'Online Consultation', icon: PhoneCall },
      home_visit: { label: lang === 'ar' ? 'زيارة منزلية' : 'Home Visit', icon: House },
    };
    return types.filter((type) => definitions[type]).map((value) => ({ value, ...definitions[value] }));
  }, [doctor?.available_appointment_types, lang]);
  const selectedPatientAddress = React.useMemo(
    () => patientAddresses.find((address) => String(address.id) === String(selectedAddressId)) || null,
    [patientAddresses, selectedAddressId]
  );
  const waitingAvailabilityLabel = React.useMemo(() => {
    if (!doctor?.first_waiting_appointment_available) return '';

    try {
      return format(new Date(doctor.first_waiting_appointment_available), 'EEEE, MMMM d - h:mm a', { locale });
    } catch (error) {
      return doctor.first_waiting_appointment_available;
    }
  }, [doctor?.first_waiting_appointment_available, locale]);
  
  // Create a stable date string for the useEffect dependency
  const selectedDateStr = format(actualSelectedDate, 'yyyy-MM-dd');

  useEffect(() => {
    const fetchDoctor = async () => {
      setLoading(true);
      try {
        const response = await doctorService.getById(id);
        const rawDoctor = response.data.data || response.data;
        const resolvedServices = await buildDoctorServicesTree(rawDoctor.id, lang);
        const fallbackServices = normalizeDoctorServices(rawDoctor.services, lang);
        const clinicName = rawDoctor.clinic ? getLocalizedValue(rawDoctor.clinic.name, lang) : '';
        const clinicAddress = rawDoctor.clinic
          ? getLocalizedValue(rawDoctor.clinic.address, lang) || rawDoctor.clinic.address || clinicName
          : rawDoctor.address || '';
        
        // Transform doctor data to handle localized fields
        const transformedDoctor = {
          id: rawDoctor.id,
          name: getDoctorDisplayName(rawDoctor, lang),
          name_ar: getDoctorDisplayName(rawDoctor, 'ar'),
          name_en: getDoctorDisplayName(rawDoctor, 'en'),
          specialty: {
            id: rawDoctor.specialties?.[0]?.id || rawDoctor.specialty?.id,
            name: getSpecialtyName(rawDoctor, lang),
            name_ar: getSpecialtyName(rawDoctor, 'ar'),
            name_en: getSpecialtyName(rawDoctor, 'en'),
          },
          rating: rawDoctor.rate || rawDoctor.rating || 0,
          reviews_count: rawDoctor.total_reviews || rawDoctor.reviews_count || 0,
          experience_years: rawDoctor.experience_years || 0,
          address: clinicName || rawDoctor.address || '',
          mapHref: getMapHref({
            mapUrl:
              rawDoctor.clinic?.google_map_url ||
              rawDoctor.clinic?.map_url ||
              rawDoctor.clinic?.google_maps_url ||
              rawDoctor.clinic_map_url ||
              rawDoctor.google_map_url ||
              rawDoctor.map_url ||
              rawDoctor.google_maps_url,
            latitude: rawDoctor.clinic?.latitude || rawDoctor.latitude,
            longitude: rawDoctor.clinic?.longitude || rawDoctor.longitude,
            address: clinicAddress,
          }),
          phone: rawDoctor.clinic?.phone_number || rawDoctor.phone || '',
          email: rawDoctor.email || '',
          image: normalizeImageValue(rawDoctor.image || rawDoctor.image_url || rawDoctor.avatar || rawDoctor.media?.[0]?.url || rawDoctor.media?.[0]?.thumb || rawDoctor.media?.[0]?.icon || ''),
          gender: rawDoctor.gender || rawDoctor.sex || '',
          // Keep raw description for HTML rendering
          bio: rawDoctor.description ? (typeof rawDoctor.description === 'object' 
            ? (rawDoctor.description[lang] || rawDoctor.description['ar'] || rawDoctor.description['en'] || '')
            : rawDoctor.description) : (rawDoctor.bio || ''),
          bio_ar: rawDoctor.description ? (typeof rawDoctor.description === 'object'
            ? (rawDoctor.description['ar'] || '')
            : rawDoctor.description) : (rawDoctor.bio_ar || ''),
          consultation_fee: rawDoctor.price || rawDoctor.consultation_fee || 0,
          doctor_currency: rawDoctor.doctor_currency || rawDoctor.currency || rawDoctor.currency_name || 'SYP',
          currency: rawDoctor.currency || rawDoctor.doctor_currency || rawDoctor.currency_name || 'SYP',
          currency_name: rawDoctor.currency_name || rawDoctor.currency || rawDoctor.doctor_currency || 'SYP',
          currency_symbol: rawDoctor.currency_symbol,
          available: rawDoctor.available || false,
          languages: rawDoctor.languages || [],
          clinic: rawDoctor.clinic ? {
            id: rawDoctor.clinic.id,
            name: getLocalizedValue(rawDoctor.clinic.name, lang),
          } : null,
          // Store availability hours for slot generation
          availability_hours: rawDoctor.availability_hours || [],
          waiting_time: rawDoctor.waiting_time || 0,
          enable_at_clinic: rawDoctor.enable_at_clinic ?? true,
          enable_at_customer_address: !!rawDoctor.enable_at_customer_address,
          available_appointment_types: rawDoctor.available_appointment_types || [],
          home_visit_fee: Number(rawDoctor.home_visit_fee || 0),
          home_visit_discount_fee: Number(rawDoctor.home_visit_discount_fee || 0),
          enable_online_consultation: rawDoctor.enable_online_consultation || false,
          enable_waiting_appointments: !!rawDoctor.enable_waiting_appointments,
          waiting_appointments: Number(rawDoctor.waiting_appointments || 0),
          waiting_number: Number(rawDoctor.waiting_number || 0),
          show_waiting_number_to_patient: !!rawDoctor.show_waiting_number_to_patient,
          can_book_waiting_appointment: rawDoctor.can_book_waiting_appointment ?? !!rawDoctor.enable_waiting_appointments,
          first_waiting_appointment_available: rawDoctor.first_waiting_appointment_available || '',
          waiting_appointment_date: rawDoctor.waiting_appointment_date || '',
          services: resolvedServices.length > 0 ? resolvedServices : fallbackServices,
        };
        
        setSelectedServicePath([]);
        setDoctor(transformedDoctor);
      } catch (error) {
        console.error('Failed to fetch doctor:', error);
        setDoctor(null);
        setSelectedServicePath([]);
      } finally {
        setLoading(false);
      }
    };
    fetchDoctor();
  }, [id, lang]);

  useEffect(() => {
    if (availableAppointmentTypes.length === 0) {
      setSelectedAppointmentType('regular');
      return;
    }

    if (!availableAppointmentTypes.some((option) => option.value === selectedAppointmentType)) {
      setSelectedAppointmentType(availableAppointmentTypes[0].value);
    }
  }, [availableAppointmentTypes, selectedAppointmentType]);

  useEffect(() => {
    if (bookingMode === 'waiting') {
      setSelectedAppointmentType('regular');
      setSelectedSlot(null);
      setSelectedServicePath([]);
    }

    if (bookingMode === 'waiting' && !doctor?.enable_waiting_appointments) {
      setBookingMode('scheduled');
    }
  }, [bookingMode, doctor?.enable_waiting_appointments]);

  // Fetch patient profiles (family members) for the logged in user
  useEffect(() => {
    if (!isAuthenticated || !user?.id || isDoctorUser) {
      setPatients([]);
      setSelectedPatient(null);
      return;
    }
    
    const fetchPatients = async () => {
      setPatientsLoading(true);
      try {
        const response = await patientService.getByUserId(user.id);
        const patientsData = response.data?.data || response.data || [];
        
        if (Array.isArray(patientsData) && patientsData.length > 0) {
          setPatients(patientsData);
          // Select default patient or first one
          const defaultPatient = patientsData.find(p => p.is_default) || patientsData[0];
          setSelectedPatient(defaultPatient);
        }
      } catch (error) {
        console.error('Failed to fetch patients:', error);
      } finally {
        setPatientsLoading(false);
      }
    };
    
    fetchPatients();
  }, [isAuthenticated, isDoctorUser, user?.id]);

  useEffect(() => {
    if (!isAuthenticated || !user?.id || isDoctorUser) {
      setPatientAddresses([]);
      setSelectedAddressId('');
      return;
    }

    const fetchAddresses = async () => {
      setAddressesLoading(true);
      try {
        const response = await addressService.getAll();
        const rows = extractAddressRows(response.data);
        setPatientAddresses(rows);
        setSelectedAddressId((currentValue) => {
          if (rows.some((address) => String(address.id) === String(currentValue))) {
            return currentValue;
          }

          return rows[0]?.id ? String(rows[0].id) : '';
        });
      } catch (error) {
        console.error('Failed to fetch patient addresses:', error);
        setPatientAddresses([]);
        setSelectedAddressId('');
      } finally {
        setAddressesLoading(false);
      }
    };

    fetchAddresses();
  }, [isAuthenticated, isDoctorUser, user?.id]);

  // Fetch slots from availability_hours API based on selected date
  useEffect(() => {
    if (!doctor || !id) return;
    if (isWaitingBooking) {
      setAvailableSlots([]);
      setSelectedSlot(null);
      return;
    }
    if (requiresFinalServiceSelection) {
      setAvailableSlots([]);
      setSelectedSlot(null);
      return;
    }
    
    const fetchSlots = async () => {
      setSlotsLoading(true);
      setSelectedSlot(null);
      
      try {
        // Format date as YYYY-MM-DD for the API
        const dateStr = format(actualSelectedDate, 'yyyy-MM-dd');
        
        // Call the availability_hours API endpoint
        const response = await doctorService.getAvailableSlots(
          id,
          dateStr,
          selectedServiceId,
          selectedAppointmentType,
        );
        const slotsData = response.data?.data || response.data || [];
        
        if (Array.isArray(slotsData) && slotsData.length > 0) {
          // Transform API response to slot objects
          // API format: [[datetime, isAvailable, isBooked], ...]
          const slots = slotsData.map(slot => {
            const dateTime = new Date(slot[0]);
            const isAvailable = slot[1];
            const isBooked = slot[2];
            
            // Format time as 12-hour format
            const hour = dateTime.getHours();
            const minute = dateTime.getMinutes();
            const hour12 = hour % 12 || 12;
            const ampm = hour < 12 ? 'AM' : 'PM';
            const formattedMinute = String(minute).padStart(2, '0');
            const timeStr = `${hour12}:${formattedMinute} ${ampm}`;
            
            return {
              time: timeStr,
              datetime: slot[0],
              available: isAvailable && !isBooked, // Available only if isAvailable=true AND isBooked=false
              isBooked: isBooked,
            };
          });
          
          setAvailableSlots(slots);
        } else if (slotsData?.slots && Array.isArray(slotsData.slots)) {
          const slots = slotsData.slots.map((slot) => {
            const dateTime = parseApiDateTime(slot.start || slot.start_at);
            if (!dateTime) return null;
            const hour = dateTime.getHours();
            const minute = dateTime.getMinutes();
            const hour12 = hour % 12 || 12;
            const ampm = hour < 12 ? 'AM' : 'PM';
            const formattedMinute = String(minute).padStart(2, '0');
            return {
              time: `${hour12}:${formattedMinute} ${ampm}`,
              datetime: slot.start || slot.start_at,
              available: !!slot.available,
              isBooked: !slot.available,
            };
          }).filter(Boolean);
          setAvailableSlots(slots);
        } else {
          // No slots available for this day
          setAvailableSlots([]);
        }
      } catch (error) {
        console.error('Failed to fetch availability hours:', error);
        // Fallback to client-side slot generation if API fails
        const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const selectedDayIndex = actualSelectedDate.getDay();
        const selectedDayName = dayNames[selectedDayIndex];
        
        const dayAvailability = doctor.availability_hours?.find(h => h.day === selectedDayName);
        
        if (dayAvailability) {
          const slots = [];
          const startParts = dayAvailability.start_at.split(':');
          const endParts = dayAvailability.end_at.split(':');
          const interval = parseInt(doctor.waiting_time) || 30;
          
          let currentHour = parseInt(startParts[0], 10);
          let currentMinute = parseInt(startParts[1], 10);
          const endHour = parseInt(endParts[0], 10);
          const endMinute = parseInt(endParts[1], 10);
          
          while (currentHour < endHour || (currentHour === endHour && currentMinute < endMinute)) {
            const hour12 = currentHour % 12 || 12;
            const ampm = currentHour < 12 ? 'AM' : 'PM';
            const formattedMinute = String(currentMinute).padStart(2, '0');
            const timeStr = `${hour12}:${formattedMinute} ${ampm}`;
            
            slots.push({
              time: timeStr,
              available: true,
              isBooked: false,
            });
            
            currentMinute = currentMinute + interval;
            if (currentMinute >= 60) {
              currentHour = currentHour + Math.floor(currentMinute / 60);
              currentMinute = currentMinute % 60;
            }
          }
          
          setAvailableSlots(slots);
        } else {
          setAvailableSlots([]);
        }
      } finally {
        setSlotsLoading(false);
      }
    };
    
    fetchSlots();
  }, [doctor, id, selectedDateStr, actualSelectedDate, selectedServiceId, selectedAppointmentType, requiresFinalServiceSelection, isWaitingBooking]);

  const handleSelectGeneralAppointment = () => {
    setSelectedServicePath([]);
    setSelectedSlot(null);
  };

  const handleSelectServiceAtLevel = (parentPath, service) => {
    setSelectedServicePath([...parentPath, service]);
    setSelectedSlot(null);
  };

  const handleBookAppointment = async () => {
    // CRITICAL: Prevent duplicate submissions using ref (survives re-renders)
    if (isBookingInProgress.current) {
      console.log('Booking already in progress, ignoring duplicate click');
      return;
    }
    
    if (!isAuthenticated) {
      toast.info(t('common.login'), { description: 'Please login to book an appointment' });
      navigate('/login');
      return;
    }

    if (!isWaitingBooking && !selectedSlot) {
      toast.error(t('common.error'), { description: 'Please select a time slot' });
      return;
    }

    if (!isWaitingBooking && requiresFinalServiceSelection) {
      toast.error(t('common.error'), {
        description: lang === 'ar' ? 'اختر الخدمة النهائية قبل متابعة الحجز' : 'Choose the final service before booking',
      });
      return;
    }

    // Set ref BEFORE state to prevent any race conditions
    isBookingInProgress.current = true;
    setBookingLoading(true);
    
    try {
      // Build the appointment payload to match Flutter mobile app structure
      // The backend expects full doctor, patient objects AND user_id at root level
      
      // Parse the selected time to create a proper datetime
      const waitingStartAt = doctor?.first_waiting_appointment_available
        ? new Date(doctor.first_waiting_appointment_available)
        : null;
      const appointmentDateTime = waitingStartAt && !Number.isNaN(waitingStartAt.getTime())
        ? waitingStartAt
        : new Date(actualSelectedDate);
      if (!isWaitingBooking && selectedSlot?.time) {
        const timeParts = selectedSlot.time.match(/(\d+):(\d+)\s*(AM|PM)?/i);
        if (timeParts) {
          let hours = parseInt(timeParts[1], 10);
          const minutes = parseInt(timeParts[2], 10);
          const period = timeParts[3]?.toUpperCase();
          
          if (period === 'PM' && hours !== 12) hours += 12;
          if (period === 'AM' && hours === 12) hours = 0;
          
          appointmentDateTime.setHours(hours, minutes, 0, 0);
        }
      }
      
      // Calculate end time (add session duration or default 30 minutes)
      const sessionDuration =
        selectedService?.durationMinutes || doctor?.session_duration || doctor?.waiting_time || 30;
      const endDateTime = new Date(appointmentDateTime.getTime() + sessionDuration * 60000);
      
      // Get patient - use selected patient from dropdown or fallback to user's default
      const patient = selectedPatient || patients[0];
      const patientId = patient?.id || user?.patient_id || user?.default_patient_id;
      
      if (!patientId) {
        toast.error(
          lang === 'ar' ? 'يرجى إنشاء ملف مريض أولاً' : 'Please create a patient profile first',
          { description: lang === 'ar' ? 'انتقل إلى أفراد العائلة لإضافة مريض' : 'Go to Family Members to add a patient' }
        );
        setBookingLoading(false);
        return;
      }
      
      if (selectedAppointmentType === 'home_visit' && !selectedPatientAddress) {
        toast.error(t('common.error'), {
          description: lang === 'ar' ? 'يرجى اختيار عنوان محفوظ للمريض' : 'Please choose a saved patient address',
        });
        setBookingLoading(false);
        return;
      }

      if (isWaitingBooking && doctor?.can_book_waiting_appointment === false) {
        toast.error(t('common.error'), {
          description: lang === 'ar' ? 'لا توجد مواعيد انتظار متاحة حالياً' : 'No waiting appointments are currently available',
        });
        setBookingLoading(false);
        return;
      }

      const isAddressAppointment = selectedAppointmentType === 'home_visit' && !isWaitingBooking;
      const addressId = isAddressAppointment ? selectedPatientAddress?.id || null : null;

      // Match the mobile payload exactly to avoid Odoo compatibility issues.
      const bookingPayload = {
        doctor_id: doctor?.id || id,
        patient_id: patientId,
        doctor_service_id: !isWaitingBooking ? selectedServiceId || undefined : undefined,
        // Appointment times - format: "YYYY-MM-DD HH:MM:SS"
        appointment_date: appointmentDateTime.toISOString(),
        duration: sessionDuration,
        appointment_type: isWaitingBooking
          ? 'waiting'
          : selectedAppointmentType,
        notes: '',
        ...(addressId ? { address_id: addressId } : {}),
      };
      
      console.log('Booking payload:', bookingPayload);
      
      await appointmentService.book(bookingPayload);
      toast.success(t('common.success'), {
        description: isWaitingBooking
          ? (lang === 'ar' ? 'تم حجز موعد الانتظار بنجاح' : 'Waiting appointment booked successfully!')
          : 'Appointment booked successfully!',
      });
      navigate('/appointments');
    } catch (error) {
      console.error('Booking error:', error.response?.data || error);
      const errorMessage = error.response?.data?.message || 'Failed to book appointment';
      
      // Provide more helpful error messages based on backend response
      let userFriendlyMessage = errorMessage;
      if (errorMessage.includes('already has an appointment') || errorMessage.includes('selected time')) {
        userFriendlyMessage = lang === 'ar' 
          ? 'لديك موعد آخر في هذا الوقت. يرجى اختيار وقت مختلف.'
          : 'You already have another appointment at this time. Please choose a different time slot.';
      } else if (errorMessage.includes('Undefined array key')) {
        userFriendlyMessage = lang === 'ar'
          ? 'حدث خطأ في الخادم. يرجى المحاولة مرة أخرى.'
          : 'A server error occurred. Please try again later.';
      }
      
      toast.error(t('common.error'), { description: userFriendlyMessage });
    } finally {
      setBookingLoading(false);
      // Reset ref after a short delay to allow state to settle
      setTimeout(() => {
        isBookingInProgress.current = false;
      }, 1000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="doctor-detail-loading">
        <div className="bg-white dark:bg-slate-800 border-b dark:border-slate-700">
          <div className="container mx-auto px-4 py-6">
            <Skeleton className="h-8 w-32 mb-4" />
            <div className="flex gap-6">
              <Skeleton className="h-32 w-32 rounded-xl" />
              <div className="flex-1 space-y-3">
                <Skeleton className="h-6 w-1/3" />
                <Skeleton className="h-4 w-1/4" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!doctor) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center px-4">
        <Card className="max-w-md w-full dark:bg-slate-800 border-0 shadow-sm">
          <CardContent className="p-6 text-center">
            <p className="text-slate-600 dark:text-slate-300 mb-4">
              {lang === 'ar' ? 'تعذر تحميل بيانات الطبيب' : 'Doctor data could not be loaded'}
            </p>
            <Button variant="outline" onClick={() => navigate(-1)}>
              <ChevronLeft className="h-4 w-4 mr-2" />
              {t('common.back')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="doctor-detail-page">
      {/* Header Section */}
      <div className="bg-white dark:bg-slate-800 border-b dark:border-slate-700">
        <div className="container mx-auto px-4 py-6">
          <Button variant="ghost" size="sm" className="mb-4 -ml-2" onClick={() => navigate(-1)} data-testid="back-button">
            <ChevronLeft className="h-4 w-4 mr-1" />
            {t('common.back')}
          </Button>

          <div className="flex flex-col md:flex-row gap-6">
            <Avatar className="h-32 w-32 rounded-xl">
              <AvatarImage
                src={doctor.image || getDefaultDoctorImage(doctor.gender)}
                alt={doctor.name}
                className="object-cover"
                onError={handleImageFallback(getDefaultDoctorImage(doctor.gender))}
              />
              <AvatarFallback className="rounded-xl bg-teal-600 text-white text-2xl">{doctor.name?.charAt(0)}</AvatarFallback>
            </Avatar>

            <div className="flex-1">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h1 className="text-2xl font-bold text-slate-900 mb-1" data-testid="doctor-name">
                    {isRTL && doctor.name_ar ? doctor.name_ar : doctor.name}
                  </h1>
                  <p className="text-teal-600 font-medium mb-2">
                    {isRTL && doctor.specialty?.name_ar ? doctor.specialty.name_ar : doctor.specialty?.name}
                  </p>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600 dark:text-slate-400">
                    <div className="flex items-center gap-1">
                      <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                      <span className="font-semibold">{doctor.rating}</span>
                      <span className="text-slate-400">({doctor.reviews_count} {t('doctors.reviews')})</span>
                    </div>
                    {doctor.experience_years > 0 && (
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4 text-slate-400" />
                        <span>{doctor.experience_years} {t('doctors.years')} {t('doctors.experience')}</span>
                      </div>
                    )}
                  </div>
                </div>
                {doctor.available ? (
                  <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                    {t('doctors.available')}
                  </Badge>
                ) : (
                  <Badge variant="secondary">{t('doctors.notAvailable')}</Badge>
                )}
              </div>
              <div className="mt-4 flex flex-wrap gap-4 text-sm text-slate-600 dark:text-slate-400">
                {doctor.address && (
                  doctorMapHref ? (
                    <a
                      href={doctorMapHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center gap-1 hover:text-teal-600 hover:underline dark:hover:text-teal-300"
                      aria-label={`${lang === 'ar' ? 'فتح الموقع على الخريطة' : 'Open location on map'} ${doctor.address}`}
                    >
                      <MapPin className="h-4 w-4 text-slate-400 group-hover:text-teal-600 dark:group-hover:text-teal-300" />
                      <span>{doctor.address}</span>
                    </a>
                  ) : (
                    <div className="flex items-center gap-1">
                      <MapPin className="h-4 w-4 text-slate-400" />
                      <span>{doctor.address}</span>
                    </div>
                  )
                )}
                {doctor.phone && (
                  doctorPhoneHref ? (
                    <a
                      href={doctorPhoneHref}
                      className="group flex items-center gap-1 hover:text-teal-600 hover:underline dark:hover:text-teal-300"
                      aria-label={`${t('common.call', 'Call')} ${doctor.phone}`}
                    >
                      <Phone className="h-4 w-4 text-slate-400 group-hover:text-teal-600 dark:group-hover:text-teal-300" />
                      <span dir="ltr">{doctor.phone}</span>
                    </a>
                  ) : (
                    <div className="flex items-center gap-1">
                      <Phone className="h-4 w-4 text-slate-400" />
                      <span>{doctor.phone}</span>
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Column - Doctor Details */}
          <div className="lg:col-span-2 space-y-6">
            <Tabs defaultValue="about" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="about">{t('doctors.about')}</TabsTrigger>
                <TabsTrigger value="education">{t('doctors.education')}</TabsTrigger>
                <TabsTrigger value="reviews">{t('doctors.reviews')}</TabsTrigger>
              </TabsList>

              <TabsContent value="about" className="mt-4">
                <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
                  <CardHeader>
                    <CardTitle className="text-lg">{t('doctors.aboutDoctor')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {/* Render bio as HTML since API returns HTML content */}
                    <div 
                      className="text-slate-600 leading-relaxed prose prose-slate max-w-none"
                      dangerouslySetInnerHTML={{ 
                        __html: isRTL && doctor.bio_ar ? doctor.bio_ar : doctor.bio 
                      }}
                    />
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="education" className="mt-4">
                <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
                  <CardHeader>
                    <CardTitle className="text-lg">{t('doctors.education')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {lang === 'ar' ? 'لا توجد بيانات تعليمية متاحة حالياً' : 'No education information is available right now'}
                    </p>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="reviews" className="mt-4">
                <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
                  <CardHeader>
                    <CardTitle className="text-lg">{t('doctors.patientReviews')}</CardTitle>
                    <CardDescription>
                      {t('doctors.basedOnReviews', { count: doctor.reviews_count })}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {doctor.reviews_count > 0 || Number(doctor.rating) > 0 ? (
                      <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                        <div className="text-center">
                          <div className="text-4xl font-bold text-slate-900 dark:text-white">{doctor.rating}</div>
                          <div className="flex items-center justify-center mt-1">
                            {[1, 2, 3, 4, 5].map((value) => (
                              <Star
                                key={value}
                                className={`h-4 w-4 ${
                                  value <= Math.round(Number(doctor.rating || 0))
                                    ? 'fill-amber-400 text-amber-400'
                                    : 'text-slate-300'
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                        <div className="flex-1 text-sm text-slate-600 dark:text-slate-400">
                          <p>{t('doctors.patientsReviewedDoctor', { count: doctor.reviews_count })}</p>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        {lang === 'ar' ? 'لا توجد تقييمات متاحة حالياً' : 'No reviews are available right now'}
                      </p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Right Column - Booking */}
          <div className="doctor-booking-theme space-y-6">
            <Card className="theme-panel-strong overflow-hidden">
              <CardContent className="p-0">
                <div className="flex items-center justify-between gap-4 border-b border-slate-200 bg-gradient-to-r from-indigo-500/10 to-cyan-400/10 px-5 py-4 dark:border-white/10">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-400">
                      {lang === 'ar' ? 'رسوم الحجز' : 'Consultation Fee'}
                    </p>
                    <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                      {lang === 'ar' ? 'السعر الحالي للجلسة المختارة' : 'Current price for the selected booking'}
                    </p>
                  </div>
                  <span className="rounded-full bg-white px-4 py-2 text-2xl font-bold text-cyan-600 dark:bg-white/10 dark:text-cyan-300">
                    {formatPriceLabel(selectedService?.price || doctor.consultation_fee || 0, doctor)}
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card className="theme-panel-strong sticky top-24 overflow-hidden">
              <CardHeader className="border-b border-slate-200 bg-white/70 pb-5 dark:border-white/10 dark:bg-white/[0.03]">
                <CardTitle className="text-lg flex items-center gap-2 text-slate-900 dark:text-white">
                  <span className="rounded-xl bg-indigo-500/15 p-2 text-indigo-300">
                    <CalendarIcon className="h-5 w-5" />
                  </span>
                  {t('appointments.bookNew')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5 p-5">
                {doctor?.enable_waiting_appointments && (
                  <BookingStepCard
                    step={lang === 'ar' ? 'نمط الحجز' : 'Booking mode'}
                    title={lang === 'ar' ? 'اختر طريقة الحجز' : 'Choose booking flow'}
                    description={
                      lang === 'ar'
                        ? 'يمكنك متابعة الحجز العادي مع اختيار الوقت، أو حجز موعد انتظار عند توفره.'
                        : 'Continue with regular scheduling or switch to a waiting appointment when available.'
                    }
                  >
                    <div className="grid gap-3 md:grid-cols-2">
                      <button
                        type="button"
                        className={cn(
                          'rounded-[24px] border p-4 text-start transition-all',
                          bookingMode === 'scheduled'
                            ? 'border-cyan-400/50 bg-cyan-400/10 ring-1 ring-cyan-400/30'
                            : 'border-white/10 bg-white/[0.03] hover:border-white/20 hover:bg-white/[0.05]'
                        )}
                        onClick={() => setBookingMode('scheduled')}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="space-y-2">
                            <p className="text-base font-semibold text-white">{lang === 'ar' ? 'موعد مجدول' : 'Scheduled appointment'}</p>
                            <p className="text-sm leading-6 text-slate-300">
                              {lang === 'ar'
                                ? 'اختيار اليوم والوقت ونوع المعاينة بالطريقة المعتادة.'
                                : 'Pick the day, time, and appointment type as usual.'}
                            </p>
                          </div>
                          <CalendarIcon className="h-5 w-5 text-cyan-300" />
                        </div>
                      </button>
                      <button
                        type="button"
                        className={cn(
                          'rounded-[24px] border p-4 text-start transition-all',
                          bookingMode === 'waiting'
                            ? 'border-amber-400/50 bg-amber-400/10 ring-1 ring-amber-400/30'
                            : 'border-white/10 bg-white/[0.03] hover:border-white/20 hover:bg-white/[0.05]',
                          doctor?.can_book_waiting_appointment === false && 'opacity-70'
                        )}
                        onClick={() => setBookingMode('waiting')}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="space-y-2">
                            <p className="text-base font-semibold text-white">{lang === 'ar' ? 'موعد انتظار' : 'Waiting appointment'}</p>
                            <p className="text-sm leading-6 text-slate-300">
                              {doctor?.can_book_waiting_appointment === false
                                ? (lang === 'ar'
                                  ? 'لا توجد مواعيد انتظار متاحة حالياً لهذا الطبيب.'
                                  : 'There are no waiting appointments available for this doctor right now.')
                                : waitingAvailabilityLabel
                                  ? (lang === 'ar' ? `أقرب موعد انتظار متاح: ${waitingAvailabilityLabel}` : `Next waiting slot: ${waitingAvailabilityLabel}`)
                                  : (lang === 'ar'
                                    ? 'سيتم حجز أول موعد انتظار متاح وفق إعدادات الطبيب.'
                                    : 'The first available waiting slot will be booked based on the doctor settings.')}
                            </p>
                          </div>
                          <TimerReset className="h-5 w-5 text-amber-300" />
                        </div>
                      </button>
                    </div>
                  </BookingStepCard>
                )}
                {visibleServices.length > 0 && !isWaitingBooking && (
                  <BookingStepCard
                    step={lang === 'ar' ? 'الخطوة 1' : 'Step 1'}
                    title={lang === 'ar' ? 'اختر الخدمة' : 'Choose service'}
                    description={
                      lang === 'ar'
                        ? 'ابدأ بالخدمات الرئيسية، وعند اختيار خدمة تحتوي على تفرعات سنعرض لك الخدمات الفرعية الخاصة بها فقط.'
                        : 'Start with the main services. If a service has children, we will reveal only its related sub services.'
                    }
                  >

                    <button
                      type="button"
                      onClick={handleSelectGeneralAppointment}
                      className={cn(
                        'w-full rounded-[24px] border p-5 text-start transition-all duration-200',
                        isGeneralAppointmentSelected
                          ? 'border-cyan-400/50 bg-cyan-400/10 shadow-[0_18px_40px_rgba(34,211,238,0.14)] ring-1 ring-cyan-400/30'
                          : 'border-white/10 bg-white/[0.03] hover:border-white/20 hover:bg-white/[0.05]'
                      )}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-4">
                          <div className="rounded-2xl bg-cyan-400/10 p-4 text-cyan-300">
                            <CalendarIcon className="h-8 w-8" />
                          </div>
                          <div className="space-y-2">
                            <h4 className="text-xl font-bold tracking-tight text-white">
                              {lang === 'ar' ? 'موعد عام' : 'General appointment'}
                            </h4>
                            <p className="max-w-md text-sm leading-7 text-slate-300">
                              {lang === 'ar'
                                ? 'استخدم المواعيد العادية المتاحة للطبيب'
                                : 'Use the available standard appointments'}
                            </p>
                            <div className="flex flex-wrap gap-2 text-xs font-medium text-slate-500">
                              <span className="rounded-full bg-slate-700/80 px-3 py-1 text-slate-100">
                                {formatPriceLabel(doctor?.consultation_fee || 0, doctor)}
                              </span>
                              {doctor?.waiting_time > 0 && (
                                <span className="rounded-full bg-slate-700/80 px-3 py-1 text-slate-100">
                                  {doctor.waiting_time} {lang === 'ar' ? 'دقيقة' : 'Minutes'}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        <span
                          className={cn(
                            'mt-1 flex h-7 w-7 items-center justify-center rounded-full border-2 transition-colors',
                            isGeneralAppointmentSelected
                              ? 'border-cyan-400 bg-cyan-400/15 text-cyan-300'
                              : 'border-slate-500 text-transparent'
                          )}
                        >
                          <CircleDot className="h-4 w-4" />
                        </span>
                      </div>
                    </button>

                    <div className="space-y-4">
                      {serviceLevels.map((level) => (
                        <div
                          key={level.key}
                          className="space-y-3"
                        >
                          <div className="flex items-center gap-2 text-sm font-semibold text-slate-100">
                            <span className="rounded-full bg-cyan-400/10 p-2 text-cyan-300">
                              <Layers3 className="h-4 w-4" />
                            </span>
                            <div>
                              <span>{level.title}</span>
                            </div>
                          </div>
                          <div className="flex snap-x gap-3 overflow-x-auto pb-1">
                            {level.services.map((service) => (
                              <div
                                key={`${level.key}-${service.id}`}
                                className="min-w-full flex-1 snap-start"
                              >
                                <ServiceChoiceRow
                                  service={service}
                                  selected={selectedServicePath[level.levelIndex]?.id === service.id}
                                  onClick={() => handleSelectServiceAtLevel(level.parentPath, service)}
                                  showChildrenHint={getVisibleServices(service.children || []).length > 0}
                                  lang={lang}
                                  currencySource={doctor}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="grid gap-3 rounded-[24px] border border-white/10 bg-white/[0.03] p-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
                      <div className="space-y-2">
                        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                          {lang === 'ar' ? 'الاختيار الحالي' : 'Current selection'}
                        </p>
                        <p className="text-base font-semibold text-white">{bookingSelectionPathLabel}</p>
                        <div className="flex flex-wrap gap-2 text-xs font-medium text-slate-300">
                          <span className="rounded-full bg-slate-700/80 px-3 py-1">
                            {formatPriceLabel(currentBookingPrice, doctor)}
                          </span>
                          <span className="rounded-full bg-slate-700/80 px-3 py-1">
                            {currentBookingDuration} {lang === 'ar' ? 'دقيقة' : 'Minutes'}
                          </span>
                          <span
                            className={cn(
                              'rounded-full px-3 py-1',
                              scheduleSelectionLocked
                                ? 'bg-amber-100 text-amber-800 ring-1 ring-amber-200 dark:bg-amber-500/15 dark:text-amber-100 dark:ring-amber-300/20'
                                : 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-100 dark:ring-emerald-300/20'
                            )}
                          >
                            {scheduleSelectionLocked
                              ? lang === 'ar'
                                ? 'بانتظار الخدمة النهائية'
                                : 'Waiting for final service'
                              : lang === 'ar'
                                ? 'جاهز لاختيار الموعد'
                                : 'Ready for scheduling'}
                          </span>
                        </div>
                      </div>
                      {selectedServicePath.length > 0 && (
                        <Button
                          type="button"
                          variant="outline"
                          className="border-white/10 bg-transparent text-slate-200 hover:bg-white/10 hover:text-white"
                          onClick={handleSelectGeneralAppointment}
                        >
                          {lang === 'ar' ? 'إعادة التعيين' : 'Reset'}
                        </Button>
                      )}
                    </div>



                    {requiresFinalServiceSelection && (
                      <div className="rounded-[24px] border border-amber-400/20 bg-amber-400/10 px-4 py-3 text-sm leading-6 text-amber-100">
                        {lang === 'ar'
                          ? 'اختر الخدمة النهائية من الخدمات الفرعية لإظهار المواعيد المتاحة.'
                          : 'Choose the final sub service from the selected main service to reveal the available slots.'}
                      </div>
                    )}
                  </BookingStepCard>
                )}
                {false && doctor?.enable_waiting_appointments && (
                  <BookingStepCard
                    step={lang === 'ar' ? 'نمط الحجز' : 'Booking mode'}
                    title={lang === 'ar' ? 'اختر طريقة الحجز' : 'Choose booking flow'}
                    description={
                      lang === 'ar'
                        ? 'يمكنك متابعة الحجز العادي مع اختيار الوقت، أو حجز موعد انتظار عند توفره.'
                        : 'Continue with regular scheduling or switch to a waiting appointment when available.'
                    }
                  >
                    <div className="grid gap-3 md:grid-cols-2">
                      <button
                        type="button"
                        className={cn(
                          'rounded-[24px] border p-4 text-start transition-all',
                          bookingMode === 'scheduled'
                            ? 'border-cyan-400/50 bg-cyan-400/10 ring-1 ring-cyan-400/30'
                            : 'border-white/10 bg-white/[0.03] hover:border-white/20 hover:bg-white/[0.05]'
                        )}
                        onClick={() => setBookingMode('scheduled')}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="space-y-2">
                            <p className="text-base font-semibold text-white">{lang === 'ar' ? 'موعد مجدول' : 'Scheduled appointment'}</p>
                            <p className="text-sm leading-6 text-slate-300">
                              {lang === 'ar'
                                ? 'اختيار اليوم والوقت ونوع المعاينة بالطريقة المعتادة.'
                                : 'Pick the day, time, and appointment type as usual.'}
                            </p>
                          </div>
                          <CalendarIcon className="h-5 w-5 text-cyan-300" />
                        </div>
                      </button>
                      <button
                        type="button"
                        className={cn(
                          'rounded-[24px] border p-4 text-start transition-all',
                          bookingMode === 'waiting'
                            ? 'border-amber-400/50 bg-amber-400/10 ring-1 ring-amber-400/30'
                            : 'border-white/10 bg-white/[0.03] hover:border-white/20 hover:bg-white/[0.05]',
                          doctor?.can_book_waiting_appointment === false && 'opacity-70'
                        )}
                        onClick={() => setBookingMode('waiting')}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="space-y-2">
                            <p className="text-base font-semibold text-white">{lang === 'ar' ? 'موعد انتظار' : 'Waiting appointment'}</p>
                            <p className="text-sm leading-6 text-slate-300">
                              {doctor?.can_book_waiting_appointment === false
                                ? (lang === 'ar'
                                  ? 'لا توجد مواعيد انتظار متاحة حالياً لهذا الطبيب.'
                                  : 'There are no waiting appointments available for this doctor right now.')
                                : waitingAvailabilityLabel
                                  ? (lang === 'ar' ? `أقرب موعد انتظار متاح: ${waitingAvailabilityLabel}` : `Next waiting slot: ${waitingAvailabilityLabel}`)
                                  : (lang === 'ar'
                                    ? 'سيتم حجز أول موعد انتظار متاح وفق إعدادات الطبيب.'
                                    : 'The first available waiting slot will be booked based on the doctor settings.')}
                            </p>
                          </div>
                          <TimerReset className="h-5 w-5 text-amber-300" />
                        </div>
                      </button>
                    </div>
                  </BookingStepCard>
                )}
                <BookingStepCard
                  step={lang === 'ar' ? 'الخطوة 2' : 'Step 2'}
                  title={lang === 'ar' ? 'اختر التاريخ والوقت' : 'Choose date and time'}
                  description={
                    scheduleSelectionLocked
                      ? lang === 'ar'
                        ? 'لن نعرض المواعيد قبل تحديد الخدمة النهائية حتى تكون الأوقات دقيقة.'
                        : 'We wait for the final service selection before loading time slots.'
                      : lang === 'ar'
                        ? 'اختر اليوم المناسب ثم الوقت المتاح للحجز.'
                        : 'Pick a suitable day, then choose an available time slot.'
                  }
                  muted={scheduleSelectionLocked || isWaitingBooking}
                >
                <div className={cn('space-y-5', (scheduleSelectionLocked || isWaitingBooking) && 'opacity-70')}>
                  <label className="mb-3 block text-sm font-semibold text-slate-200">
                    {isWaitingBooking
                      ? (lang === 'ar' ? 'موعد الانتظار المتوقع' : 'Expected waiting appointment')
                      : t('appointments.selectDate')}
                  </label>
                  {!isWaitingBooking ? (
                  <div className="flex flex-wrap gap-2">
                    {dateOptions.map((date, idx) => (
                      <Button
                        key={idx}
                        variant={selectedDate === idx ? "default" : "outline"}
                        size="sm"
                        className={selectedDate === idx ? 'bg-cyan-500 text-slate-950 hover:bg-cyan-400' : 'border-white/10 bg-white/5 text-slate-200 hover:bg-white/10 hover:text-white'}
                        onClick={() => { setSelectedDate(idx); setSelectedSlot(null); }}
                        disabled={scheduleSelectionLocked}
                        data-testid={`date-option-${idx}`}
                      >
                        <div className="text-center">
                          <div className="text-xs">{format(date, 'EEE', { locale })}</div>
                          <div className="text-sm font-bold">{format(date, 'd', { locale })}</div>
                        </div>
                      </Button>
                    ))}
                  </div>
                  ) : (
                    <div className="rounded-2xl border border-amber-400/20 bg-amber-400/10 px-4 py-4 text-sm text-amber-100">
                      {doctor?.can_book_waiting_appointment === false
                        ? (lang === 'ar'
                          ? 'لا توجد مواعيد انتظار متاحة لهذا الطبيب حالياً.'
                          : 'No waiting appointments are currently available for this doctor.')
                        : waitingAvailabilityLabel || (lang === 'ar'
                          ? 'سيتم تأكيد أول موعد انتظار متاح من الطبيب عند الحجز.'
                          : 'The first available waiting slot will be confirmed when you book.')}
                    </div>
                  )}
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">
                    <span className="font-semibold text-slate-200">{t('appointments.selectTime')}</span>
                    <span className="text-slate-400"> - {format(actualSelectedDate, 'EEE, MMM d', { locale })}</span>
                  </label>
                  {isWaitingBooking ? (
                    <p className="rounded-2xl border border-dashed border-amber-400/20 bg-amber-400/10 px-4 py-6 text-center text-sm text-amber-100">
                      {lang === 'ar'
                        ? 'موعد الانتظار لا يحتاج لاختيار وقت يدوي، وسيعتمد أول وقت متاح للطبيب.'
                        : 'Waiting appointments do not need a manual time selection and will use the doctor first available slot.'}
                    </p>
                  ) : slotsLoading ? (
                    <div className="grid grid-cols-3 gap-2">
                      <Skeleton className="h-10" />
                      <Skeleton className="h-10" />
                      <Skeleton className="h-10" />
                      <Skeleton className="h-10" />
                      <Skeleton className="h-10" />
                      <Skeleton className="h-10" />
                    </div>
                  ) : scheduleSelectionLocked ? (
                    <p className="rounded-2xl border border-dashed border-white/10 bg-white/5 px-4 py-6 text-center text-sm text-slate-400">
                      {lang === 'ar'
                        ? 'اختر الخدمة النهائية أولاً لعرض الأوقات المتاحة.'
                        : 'Choose the final service first to show available times.'}
                    </p>
                  ) : availableSlots.length === 0 ? (
                    <p className="py-4 text-center text-sm text-slate-400">{t('doctors.noSlotsAvailable')}</p>
                  ) : (
                    <div className="grid grid-cols-3 gap-2">
                      {availableSlots.map((slot, idx) => (
                        <Button
                          key={idx}
                          variant={selectedSlot?.time === slot.time ? "default" : "outline"}
                          size="sm"
                          className={`relative min-h-11 ${selectedSlot?.time === slot.time ? 'bg-cyan-500 text-slate-950 hover:bg-cyan-400' : 'border-white/10 bg-white/5 text-slate-100 hover:bg-white/10'} ${!slot.available ? 'cursor-not-allowed border-white/5 bg-white/[0.03] text-slate-500 opacity-60' : ''}`}
                          disabled={!slot.available}
                          onClick={() => slot.available && setSelectedSlot(slot)}
                          data-testid={`time-slot-${idx}`}
                          title={slot.isBooked ? (lang === 'ar' ? 'محجوز' : 'Booked') : ''}
                        >
                          <span className={slot.isBooked ? 'line-through' : ''}>{slot.time}</span>
                          {slot.isBooked && (
                            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[8px] px-1 rounded">
                              {lang === 'ar' ? 'محجوز' : 'Booked'}
                            </span>
                          )}
                        </Button>
                      ))}
                    </div>
                  )}
                </div>
                </BookingStepCard>

                {(selectedSlot || isWaitingBooking) && (
                  <div className="rounded-2xl border border-cyan-400/20 bg-cyan-400/10 p-3">
                    <div className="flex items-center gap-2 text-sm text-cyan-200">
                      <Check className="h-4 w-4" />
                      <span>
                        {isWaitingBooking
                          ? (waitingAvailabilityLabel || (lang === 'ar'
                            ? 'سيتم اعتماد أول موعد انتظار متاح'
                            : 'The first available waiting slot will be used'))
                          : `${format(actualSelectedDate, 'EEEE, MMMM d', { locale })} at ${selectedSlot.time}`}
                      </span>
                    </div>
                  </div>
                )}

                {availableAppointmentTypes.length > 0 && !isWaitingBooking && (
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-slate-200">
                      {lang === 'ar' ? 'نوع الموعد' : 'Appointment Type'}
                    </label>
                    <div className={cn('grid gap-2', availableAppointmentTypes.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2')}>
                      {availableAppointmentTypes.map(({ value, label, icon: Icon }) => (
                        <Button
                          key={value}
                          type="button"
                          variant={selectedAppointmentType === value ? 'default' : 'outline'}
                          className={selectedAppointmentType === value ? 'bg-cyan-500 text-slate-950 hover:bg-cyan-400' : 'border-white/10 bg-white/5 text-slate-200 hover:bg-white/10'}
                          onClick={() => setSelectedAppointmentType(value)}
                          data-testid={`btn-${value}`}
                        >
                          <Icon className="mr-2 h-4 w-4" />
                          {label}
                        </Button>
                      ))}
                    </div>
                    {selectedAppointmentType === 'online' && (
                      <p className="flex items-center gap-1 text-xs text-slate-400">
                        <PhoneCall className="h-3 w-3" />
                        {lang === 'ar' 
                          ? 'يمكنك الاتصال بالطبيب في الموعد المحدد'
                          : 'You can call the doctor at the scheduled time'}
                      </p>
                    )}
                    {selectedAppointmentType === 'home_visit' && (
                      <p className="flex items-center gap-1 text-xs text-slate-400">
                        <House className="h-3 w-3" />
                        {lang === 'ar'
                          ? 'سيتم إرسال الطلب كزيارة إلى عنوان المريض المحدد أدناه.'
                          : 'The booking will be sent as a home visit to the patient address selected below.'}
                      </p>
                    )}
                  </div>
                )}

                {/* Patient Selection - Book for self or family member */}
                {!isDoctorUser && isAuthenticated && (
                  <div>
                    <label className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-200">
                      <Users className="h-4 w-4" />
                      {lang === 'ar' ? 'حجز لـ' : 'Book for'}
                    </label>
                    {patientsLoading ? (
                      <Skeleton className="h-10 w-full" />
                    ) : patients.length === 0 ? (
                      <div className="flex items-center justify-between rounded-xl border border-white/10 bg-white/5 p-3">
                        <span className="text-sm text-slate-300">
                          {lang === 'ar' ? 'لا يوجد ملف مريض' : 'No patient profile'}
                        </span>
                        <Button variant="outline" size="sm" asChild>
                          <Link to="/family" className="flex items-center gap-1">
                            <UserPlus className="h-3 w-3" />
                            {lang === 'ar' ? 'إضافة' : 'Add'}
                          </Link>
                        </Button>
                      </div>
                    ) : (
                      <Select 
                        value={selectedPatient?.id?.toString()} 
                        onValueChange={(value) => {
                          const patient = patients.find(p => p.id?.toString() === value);
                          setSelectedPatient(patient);
                        }}
                      >
                        <SelectTrigger 
                          className="border-white/10 bg-white/5 text-white"
                          data-testid="patient-selector"
                        >
                          <SelectValue placeholder={lang === 'ar' ? 'اختر المريض' : 'Select patient'}>
                            {getPatientDisplayName(selectedPatient, lang) || (selectedPatient?.id ? `Patient ${selectedPatient.id}` : '')}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {patients.map(patient => {
                            const name = getPatientDisplayName(patient, lang);
                            return (
                              <SelectItem key={patient.id} value={patient.id?.toString()}>
                                <div className="flex items-center gap-2">
                                  <span>{name || `Patient ${patient.id}`}</span>
                                  {patient.is_default && (
                                    <Badge variant="secondary" className="text-xs">
                                      {lang === 'ar' ? 'افتراضي' : 'Default'}
                                    </Badge>
                                  )}
                                </div>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                )}

                {!isDoctorUser && isAuthenticated && selectedAppointmentType === 'home_visit' && !isWaitingBooking && (
                  <div className="space-y-3">
                    <label className="flex items-center gap-2 text-sm font-semibold text-slate-200">
                      <House className="h-4 w-4" />
                      {lang === 'ar' ? 'عنوان الزيارة' : 'Visit address'}
                    </label>
                    {addressesLoading ? (
                      <Skeleton className="h-10 w-full" />
                    ) : patientAddresses.length > 0 ? (
                      <Select value={selectedAddressId} onValueChange={setSelectedAddressId}>
                        <SelectTrigger className="border-white/10 bg-white/5 text-white" data-testid="address-selector">
                          <SelectValue placeholder={lang === 'ar' ? 'اختر عنوان المريض' : 'Select patient address'}>
                            {getPatientAddressLabel(selectedPatientAddress, lang)}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {patientAddresses.map((address) => (
                            <SelectItem key={address.id} value={String(address.id)}>
                              {getPatientAddressLabel(address, lang) || `${lang === 'ar' ? 'عنوان' : 'Address'} ${address.id}`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="rounded-xl border border-dashed border-white/10 bg-white/5 px-3 py-3 text-sm text-slate-400">
                        {lang === 'ar'
                          ? 'لا يوجد عنوان محفوظ للمريض. يرجى إضافة عنوان من التطبيق أو من صفحة العناوين أولاً.'
                          : 'No saved patient address was found. Please add one first from the app or the addresses page.'}
                      </p>
                    )}
                  </div>
                )}

                {!isDoctorUser && (
                  <Button
                    className="h-12 w-full bg-cyan-500 font-semibold text-slate-950 hover:bg-cyan-400"
                    size="lg"
                    disabled={
                      bookingLoading ||
                      (!isWaitingBooking && !selectedSlot) ||
                      (!isWaitingBooking && requiresFinalServiceSelection) ||
                      (isWaitingBooking && doctor?.can_book_waiting_appointment === false)
                    }
                    onClick={handleBookAppointment}
                    data-testid="book-appointment-button"
                  >
                    {bookingLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {t('common.loading')}
                      </>
                    ) : (
                      <>
                        <CalendarIcon className="h-4 w-4 mr-2" />
                        {t('appointments.confirmBooking')}
                      </>
                    )}
                  </Button>
                )}

                {!isDoctorUser && !isAuthenticated && (
                  <p className="text-xs text-center text-slate-500 dark:text-slate-400">
                    Please <Link to="/login" className="text-teal-600 hover:underline">{t('common.login')}</Link> to book an appointment
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoctorDetailPage;
